import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, CalendarBlank, Clock, X, HandsPraying, BookOpen, Flask, Cookie, PuzzlePiece, Coins, Sun } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { wibNow } from "@/lib/time";
import { MonthCalendar, COLOR_MAP } from "@/components/MonthCalendar";

const ICONS = { hands: HandsPraying, book: BookOpen, flask: Flask, apple: Cookie, blocks: PuzzlePiece, coins: Coins };
const WEEKDAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat"];
const TABS = [
  { key: "harian", label: "Hari Ini", icon: Sun },
  { key: "mingguan", label: "Mingguan", icon: CalendarCheck },
  { key: "bulanan", label: "Bulanan", icon: CalendarBlank },
];

export default function Jadwal() {
  const [data, setData] = useState({ daily: [], weekly: {}, monthly: [] });
  const [tab, setTab] = useState("harian");
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth()]);
  const [dayModal, setDayModal] = useState(null);
  useEffect(() => { api.get("/schedule").then((r) => setData(r.data)); }, []);

  const today = wibNow().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const todayName = wibNow().toLocaleDateString("id-ID", { weekday: "long" });
  const monthly = (data.monthly || []).filter((e) => /^\d{4}-\d{2}-\d{2}$/.test(e.date || ""));
  const dayEntries = dayModal ? monthly.filter((e) => e.date === dayModal) : [];

  return (
    <div>
      <PageHeader title="Jadwal Belajar & Bermain Hari Ini" />
      <div className="px-5 pt-4">
        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`jadwal-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === t.key ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>
              <t.icon size={16} weight="bold" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "harian" && (
          <>
            <div className="bg-primary/10 rounded-2xl px-4 py-3 mb-4">
              <p className="text-xs text-primary font-semibold uppercase tracking-wide">Hari Ini</p>
              <p className="font-heading font-bold text-stone-800">{today}</p>
            </div>
            <DailyList items={data.daily} />
          </>
        )}

        {tab === "mingguan" && (
          <div className="space-y-4" data-testid="jadwal-weekly">
            {WEEKDAYS.map((d) => {
              const items = data.weekly?.[d] || [];
              return (
                <div key={d}>
                  <p className={`text-xs font-bold px-2.5 py-1 rounded-full inline-block mb-2 ${d === todayName ? "bg-primary text-white" : "bg-primary/10 text-primary"}`}>{d}</p>
                  {items.length === 0 ? (
                    <p className="text-stone-400 text-sm pl-1">Belum ada jadwal.</p>
                  ) : (
                    <div className="space-y-2">
                      {items.map((it, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-stone-200/60 p-3 flex items-center gap-3 shadow-sm">
                          <div className="w-10 h-10 rounded-xl bg-secondary/15 flex items-center justify-center shrink-0"><Clock size={18} weight="duotone" className="text-primary" /></div>
                          <div className="flex-1"><p className="font-semibold text-stone-800 text-sm">{it.activity}</p>
                            {(it.start || it.end) && <p className="text-stone-400 text-xs">{it.start} - {it.end} WIB</p>}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "bulanan" && (
          <div data-testid="jadwal-monthly">
            <p className="text-xs text-stone-500 mb-3">Klik tanggal untuk melihat catatan kegiatan.</p>
            <MonthCalendar ym={ym} setYm={setYm} entries={monthly} onDayClick={(d) => setDayModal(d)} />
          </div>
        )}
      </div>

      {dayModal && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setDayModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">{new Date(dayModal).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
              <button onClick={() => setDayModal(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            {dayEntries.length === 0 ? <p className="text-stone-400 text-sm">Tidak ada catatan pada tanggal ini.</p> : (
              <div className="space-y-2">
                {dayEntries.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 bg-stone-50 rounded-xl p-3">
                    <span className={`w-3 h-3 rounded-full shrink-0 ${COLOR_MAP[e.color] || "bg-primary"}`} />
                    <span className="text-sm font-semibold text-stone-700">{e.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DailyList({ items }) {
  return (
    <div className="relative pl-6" data-testid="jadwal-daily">
      <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-stone-200" />
      {(items || []).map((it, i) => {
        const Icon = ICONS[it.icon] || Clock;
        return (
          <motion.div key={i} data-testid={`jadwal-item-${i}`}
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
            className="relative mb-3">
            <div className="absolute -left-6 top-4 w-4 h-4 rounded-full bg-primary border-2 border-white" />
            <div className="bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center gap-3 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-secondary/15 flex items-center justify-center"><Icon size={22} weight="duotone" className="text-primary" /></div>
              <div className="flex-1"><p className="font-semibold text-stone-800 text-sm">{it.activity}</p>
                <p className="text-stone-400 text-xs">{it.start} - {it.end} WIB</p></div>
            </div>
          </motion.div>
        );
      })}
      {(items || []).length === 0 && <p className="text-stone-400 text-sm">Belum ada jadwal harian.</p>}
    </div>
  );
}
